"use client";

import { useState } from "react";
import { capture } from "@/lib/posthog-browser";

type Status = "idle" | "submitting" | "success" | "error";

export default function InterestForm({
  campId,
  providerId,
  locationSlug,
  source = "unknown",
}: {
  campId?: string;
  providerId?: string;
  locationSlug?: string;
  source?: string;
}) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/api/interest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };

      if (!response.ok || !data.ok) {
        const message =
          data.error ??
          "Something broke on our end, try that again in a minute, or email hello@mywildroost.com.";
        console.error("[interest-form] submit failed", {
          status: response.status,
          data,
        });
        setErrorMessage(message);
        setStatus("error");
        return;
      }

      try {
        capture("interest_submitted", {
          camp_id: campId ?? null,
          provider_id: providerId ?? null,
          location_slug: locationSlug ?? null,
          source,
        });
      } catch (captureErr) {
        console.error("[interest-form] posthog capture failed", captureErr);
      }

      setStatus("success");
    } catch (err) {
      console.error("[interest-form] network error", err);
      setErrorMessage(
        "Something broke on our end, try that again in a minute, or email hello@mywildroost.com.",
      );
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="success-msg" role="status">
        <p>You&rsquo;re on the list!</p>
        <span>We&rsquo;ll let you know as soon as Wildroost is live in your area.</span>
      </div>
    );
  }

  return (
    <>
      <form className="email-form" onSubmit={handleSubmit} noValidate={false}>
        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "submitting"}
          aria-label="Email address"
        />
        <button type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Sending…" : "Notify Me"}
        </button>
      </form>
      {status === "error" && errorMessage ? (
        <p role="alert" className="form-error">
          {errorMessage}
        </p>
      ) : (
        <p className="form-note">Occasional updates as Wildroost grows. Unsubscribe anytime.</p>
      )}
    </>
  );
}
